// Bói Đê — màn bói nhanh hợp nhất. Các chủ đề nâng cao được chọn trong
// template picker tại chỗ; phiên Bói Đê cũ vẫn mở qua deep-link lịch sử.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { CalendarHeart, Compass, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TopicFlow } from "./_authenticated.tong-hop.$topic";
import { TongHopClassic } from "@/components/boi-de/tong-hop-classic";
import {
  formatVnd,
  topicAccent,
  topicDisplayName,
  topicIcon,
} from "@/components/boi-de/topic-style";
import { getSession, listBoiDeTopics, type BoiDeTopic } from "@/lib/boide-api";

export const Route = createFileRoute("/_authenticated/tong-hop/")({
  validateSearch: (search: Record<string, unknown>): { session?: string } => ({
    session: typeof search.session === "string" ? search.session : undefined,
  }),
  component: BoiDeQuickPage,
});

const FILTERS = [
  { key: "tat-ca", label: "Tất cả" },
  { key: "xem-nhanh", label: "Xem nhanh" },
  { key: "tinh-cam", label: "Tình duyên" },
  { key: "cong-danh", label: "Công danh" },
  { key: "gia-dinh", label: "Gia đình" },
  { key: "nha-cua", label: "Nhà cửa & Đời sống" },
  { key: "suc-khoe", label: "Sức khỏe" },
  { key: "kham-pha", label: "Khám phá bản thân" },
] as const;

function topicFilter(topic: BoiDeTopic): string {
  if (topic.slug.includes("suc-khoe") || topic.name.toLowerCase().includes("sức khỏe")) {
    return "suc-khoe";
  }
  return topic.group_key || "khac";
}

function TemplatePicker({
  topics,
  selected,
  onSelect,
  onClose,
}: {
  topics: BoiDeTopic[];
  selected: string;
  onSelect: (topic: BoiDeTopic) => void;
  onClose: () => void;
}) {
  // Luôn mở bằng toàn bộ danh sách: trước đây mặc định "Xem nhanh" chỉ hiện 2
  // topic, khiến các nhóm nằm ngoài vùng cuộn ngang bị hiểu là đang thiếu.
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["key"]>("tat-ca");
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return topics.filter((topic) => {
      if (filter !== "tat-ca" && topicFilter(topic) !== filter) return false;
      const displayName = topicDisplayName(topic.slug, topic.name);
      return !q || `${displayName} ${topic.name} ${topic.description}`.toLowerCase().includes(q);
    });
  }, [filter, query, topics]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm md:items-stretch md:justify-end md:p-5"
      role="dialog"
      aria-modal="true"
      aria-label="Chọn chủ đề"
    >
      <button aria-label="Đóng" className="absolute inset-0 cursor-default" onClick={onClose} />
      <section className="relative flex h-[86vh] w-full flex-col overflow-hidden rounded-t-[26px] border border-gold-soft/40 bg-background shadow-2xl md:h-full md:max-h-none md:w-[410px] md:rounded-3xl">
        <div className="flex items-center justify-between border-b border-border/40 px-4 py-3 md:px-5">
          <div>
            <h2 className="text-base font-semibold text-foreground">Chọn chủ đề luận giải</h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Tình duyên, sự nghiệp, tài lộc và nhiều chủ đề khác
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Đóng bảng chủ đề">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4 pb-[calc(2rem+env(safe-area-inset-bottom))] md:p-5">
          <label className="flex items-center gap-2 rounded-xl border border-border/50 bg-card/50 px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tìm chủ đề cần xem…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
            />
          </label>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            {FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition ${filter === item.key ? "border-primary bg-primary/15 text-foreground" : "border-border/50 bg-card/40 text-muted-foreground hover:text-foreground"}`}
              >
                {item.label}
                {item.key === "tat-ca" ? ` (${topics.length})` : ""}
              </button>
            ))}
          </div>
          {/* Vận khí miễn phí — không nằm trong boi_de_topics nên ghim riêng ở đầu danh sách. */}
          <Link
            to="/tong-hop/van-khi"
            onClick={onClose}
            className="flex w-full items-center gap-3 rounded-2xl border border-gold-soft/40 bg-card/50 p-3 text-left transition hover:border-primary/60 hover:bg-card/80"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/12">
              <CalendarHeart className="h-5 w-5 text-primary" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">Vận khí</span>
              <span className="mt-0.5 block line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
                Vận ngày, vận tuần, vận tháng âm lịch và nhãn ngày tốt
              </span>
            </span>
            <span className="shrink-0 text-[11px] font-semibold text-emerald-300">Miễn phí</span>
          </Link>
          <Link
            to="/tong-hop/la-ban"
            onClick={onClose}
            className="flex w-full items-center gap-3 rounded-2xl border border-gold-soft/40 bg-card/50 p-3 text-left transition hover:border-primary/60 hover:bg-card/80"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/12">
              <Compass className="h-5 w-5 text-primary" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">La bàn phong thuỷ</span>
              <span className="mt-0.5 block line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
                Bát Trạch theo cung phi + hướng theo khí từng ngày
              </span>
            </span>
            <span className="shrink-0 text-[11px] font-semibold text-emerald-300">Miễn phí</span>
          </Link>
          <div className="space-y-2">
            {filtered.map((topic) => {
              const Icon = topicIcon(topic.icon);
              const accent = topicAccent(topic.slug);
              return (
                <button
                  key={topic.slug}
                  type="button"
                  onClick={() => onSelect(topic)}
                  className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition hover:border-primary/60 hover:bg-card/80 ${selected === topic.slug ? "border-primary/70 bg-primary/5" : "border-border/40 bg-card/40"}`}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent.bg}`}
                  >
                    <Icon className={`topic-icon--light h-5 w-5 ${accent.text}`} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      {topicDisplayName(topic.slug, topic.name)}
                    </span>
                    <span className="mt-0.5 block line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
                      {topic.description}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold text-primary">
                    {formatVnd(topic.price)}
                  </span>
                </button>
              );
            })}
            {!filtered.length ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Không có chủ đề phù hợp.
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

function BoiDeQuickPage() {
  const { session: sessionFromUrl } = Route.useSearch();
  const navigate = useNavigate();
  const topicsQ = useQuery({ queryKey: ["boi-de-topics"], queryFn: listBoiDeTopics });

  const sessionQ = useQuery({
    queryKey: ["session", sessionFromUrl],
    queryFn: () => getSession(sessionFromUrl as string),
    enabled: Boolean(sessionFromUrl),
  });
  const palmRedirect = sessionQ.data?.metadata?.palm?.reading_id;
  useEffect(() => {
    if (!palmRedirect) return;
    navigate({ to: "/boi-chi-tay", search: { reading: palmRedirect }, replace: true });
  }, [navigate, palmRedirect]);

  const topics = (topicsQ.data ?? []).filter((topic) => topic.input_kind !== "free");
  const quick =
    topics.find((topic) => topic.slug === "hoi-nhanh") ??
    topics.find((topic) => topic.reading_kind === "quick") ??
    topics[0];
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>();
  const [pickerOpen, setPickerOpen] = useState(false);
  const selected = topics.find((topic) => topic.slug === selectedSlug) ?? quick;

  if (!sessionFromUrl && (topicsQ.isLoading || !selected)) {
    return (
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-3 px-3 py-6 md:px-4 md:py-10">
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-72 w-full rounded-2xl" />
        </div>
      </main>
    );
  }

  if (sessionFromUrl) {
    // Phiên bói chỉ tay cũng là `tong_hop` nhưng ảnh + vùng khoanh nằm ở bảng
    // riêng; mở ở đây sẽ chỉ thấy bài luận dạng chat. Đẩy về đúng trang của nó
    // (link cũ / bookmark vẫn dùng được).
    if (palmRedirect) {
      return (
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl space-y-3 px-3 py-6 md:px-4 md:py-10">
            <Skeleton className="h-28 w-full rounded-2xl" />
            <Skeleton className="h-72 w-full rounded-2xl" />
          </div>
        </main>
      );
    }
    return (
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-3 py-5 pb-24 md:px-4 md:py-8">
          <div className="mb-4 rounded-2xl border border-gold-soft/40 bg-card/50 p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-primary">Bói Đê</p>
            <h1 className="mt-1 text-xl font-semibold">Phiên đã xem</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              Phiên cũ được mở lại trong khu Bói Đê để bạn tiếp tục trò chuyện.
            </p>
          </div>
          <TongHopClassic initialSessionId={sessionFromUrl} />
        </div>
      </main>
    );
  }

  if (!selected) return null;

  return (
    <main className="app-ink-screen app-ink-screen--boi-de min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-0">
      <div className="mx-auto w-full max-w-[800px] px-4 py-3 md:px-6 md:py-10">
        <TopicFlow
          key={selected.slug}
          topic={selected}
          compactProfile={selected.min_persons === 1}
          unifiedShell
          onOpenTemplates={() => setPickerOpen(true)}
        />
      </div>
      {pickerOpen ? (
        <TemplatePicker
          topics={topics}
          selected={selected.slug}
          onSelect={(topic) => {
            setSelectedSlug(topic.slug);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </main>
  );
}
